import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Modal,
    Button,
    Select,
    FormField,
    Box,
    SpaceBetween,
    SelectProps,
    NonCancelableCustomEvent
} from '@amzn/awsui-components-react';

import { FormSchema } from 'modules/mappings/models';
import style from '../OrgMappings/OrgMappings.module.scss';

interface ReassignCostCenterOrgMappingProps {
    costCenter: string;
    orgList: string[];
    onCreateNewOrg: () => void;
    onSave: (reassignedOrg: string) => void;
    onCancel: () => void;
    currentOrg: string;
}

export const ReassignCostCenterOrgMapping = (props: ReassignCostCenterOrgMappingProps) => {
    const { onCreateNewOrg, costCenter, orgList, onSave, onCancel, currentOrg } = props;
    const { t } = useTranslation('mappings');
    const title = t('orgMappings.reassignCostCenterModalHeader');

    const InitialDropDownOptions: SelectProps.Option[] = [];
    const [orgDropDownOptions, setOrgDropDownOptions] = useState(InitialDropDownOptions);
    const [reassignedOrgName, setReassignedOrgName] = useState<SelectProps.Option | null>(null);
    const { register, setValue, errors, formState } = FormSchema.getReassignCostCenterUseForm();

    useEffect(() => {
        register({ name: 'reassignedOrg', type: 'custom' });
    }, [register]);

    useEffect(() => {
        const dropDownOptions: SelectProps.Option[] = [];
        orgList.forEach(org => {
            if (org != currentOrg) dropDownOptions.push({ label: org, value: org.replace(' ', '') });
        });
        setOrgDropDownOptions(dropDownOptions);
    }, [orgList]);

    const onOrgSelection = ({ detail: { selectedOption } }: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setReassignedOrgName(selectedOption);
        setValue('reassignedOrg', selectedOption.label ?? '', true);
    };

    const onModalSave = () => {
        if (!reassignedOrgName) {
            setValue('reassignedOrg', '', true);
        }
        if (formState.isValid && reassignedOrgName) {
            onSave(reassignedOrgName.label ?? '');
        }
    };

    const content = (
        <div className={style['modal-div']}>
            <p>{`${t('costCenter')}: ${costCenter}`}</p>
            <FormField errorText={errors?.reassignedOrg?.message}>
                <Select
                    selectedOption={reassignedOrgName}
                    options={orgDropDownOptions}
                    placeholder={t('orgMappings.reassignOrgSelectPlaceholder')}
                    onChange={onOrgSelection}
                    data-cy="reassignedOrgDropdown"
                />
            </FormField>
        </div>
    );

    return (
        <Modal
            visible={true}
            header={title}
            onDismiss={onCancel}
            data-cy="reassignCostCenter"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={onCreateNewOrg} data-cy="reassignCreateNewOrg">
                            {t('orgMappings.createNewOrg')}
                        </Button>
                        <Button variant="primary" onClick={onModalSave} data-cy="reassignSave">
                            {t('save')}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            {content}
        </Modal>
    );
};
